<script lang="ts">
    import { onDestroy, onMount } from 'svelte';
    import { page } from '$app/stores';
    import { onAuthenticationOk, onRegistrationStarted } from './callbacks';
    import {
        getPreferredOffer,
        getRegilyConfig,
        regilyRedirectionLogic,
        addBonusTermsLinksToOffers,
        deleteCookie,
        extractNativeEventDataFromCookie
    } from '$lib/services/register/utils.frontend';
    import { browser } from '$app/environment';
    import {
        REGILY_FORM_ACTIVE_HASHES,
        REGILY_ENVS,
        TRACKING_ID_COOKIE_KEY
    } from '$lib/services/register/constants';
    import { Cookies, fromDocument } from '$lib/services/helpers/cookies';
    import {
        CASUMO_REGISTRATION_ENABLED_MARKETS,
        CASUMO_TEST_REGISTRATION_ENABLED_MARKETS,
        COOKIES
    } from '$lib/services/legacy/standaloneRegistration/constants';
    import { endOfRegistrationLogin } from '$lib/services/legacy/auth/url-utils';
    import RebrandCasumoLogo from './components/RebrandCasumoLogo.svelte';
    import RebrandRegistrationBg from './components/RebrandRegistrationBg.svelte';
    import RegistrationError from './components/RegistrationError.svelte';
    import { storeDeviceInfo } from './deviceTracker/deviceTracker';
    import type { TFlowMessage } from '$lib/services/register/types';
    import { getEnvironmentHost, isTest } from '$lib/services/environment';
    import {
        onRegistrationComplete,
        sendRegistrationEndNativeEvent,
        sendRegistrationStartNativeEvents
    } from '$lib/services/native';
    import { createOrRefreshInstance } from '$lib/services/legacy/auth';
    import { MARKET_SLUGS } from '$lib/services/market/slugs';
    import type { SpineAuth } from '$lib/services/legacy/auth/spine-auth';
    import '$src/tailwind.css';
    import loaderImg from '$lib/icons/gifs/loader.gif';

    const {
        market,
        bonusTermsLinks,
        regilyModule,
        welcomeOffersExtended,
        regilyBundle,
        href,
        errorMessages,
        localCode,
        authBootstrap
    } = $page.data;

    let regilyMounted = false;
    let isLoading = true;
    let trackingId = '';
    let auth: SpineAuth | undefined;
    let error: TFlowMessage = { key: '', translations: [], icon: null };
    let cookies: Cookies;

    if (browser) {
        cookies = new Cookies(fromDocument(document));
    }
    const handleHashChange = () => {
        if (regilyMounted && window.location.hash === '') {
            window.location.href = market.slug === MARKET_SLUGS.ES ? '/' : `/${market?.slug}`;
        }
        if (REGILY_FORM_ACTIVE_HASHES.includes(window.location.hash)) {
            regilyMounted = true;
        }
    };

    let testRegisterEnabled = false;

    if (browser) {
        const urlParams = new URLSearchParams(window.location.search);
        testRegisterEnabled = urlParams.get('testRegisterEnabled') === 'true';
    }

    const isStandaloneRegistration =
        CASUMO_REGISTRATION_ENABLED_MARKETS.includes(market.slug) ||
        (CASUMO_TEST_REGISTRATION_ENABLED_MARKETS.includes(market.slug) && testRegisterEnabled);

    let errorNameCookie: string | undefined;

    const loadRegily = () => {
        if (!window?.casumoRegily) {
            return;
        }

        regilyMounted = REGILY_FORM_ACTIVE_HASHES.includes(window.location.hash);
        trackingId = cookies.get(TRACKING_ID_COOKIE_KEY) || '';

        window.addEventListener('hashchange', handleHashChange);

        const regilyEnv = isTest(window?.location?.origin) ? REGILY_ENVS.TEST : REGILY_ENVS.LIVE;

        addBonusTermsLinksToOffers(welcomeOffersExtended, bonusTermsLinks);
        const preferredWelcomeOffer = getPreferredOffer(welcomeOffersExtended);

        const preferredOfferId =
            welcomeOffersExtended?.offers?.find(
                (offer: { bonusType: string }) => offer?.bonusType === preferredWelcomeOffer
            )?.id || '';

        const res = window?.casumoRegily(
            getRegilyConfig(
                market,
                regilyModule,
                welcomeOffersExtended,
                trackingId,
                regilyEnv,
                preferredWelcomeOffer,
                preferredOfferId
            )
        );

        if (res) {
            res.on('AUTHENTICATION_OK', onAuthenticationOk);
            res.on('REGISTRATION_STARTED', onRegistrationStarted);
            res.on('REGISTRATION_COMPLETED', onRegistrationComplete);
            res.open();

            res.on('DATA_FULL_UPDATED', (regilyProps: { key: string; regilyData: unknown }) => {
                const { key, regilyData: data } = regilyProps;
                regilyRedirectionLogic(market, key, data);
            });
        }

        // Send Native events.
        sendRegistrationStartNativeEvents();
    };

    const loadCasumo = async () => {
        const tokenSet = auth?.getTokenSet();

        if (window.native) {
            cookies.set(COOKIES.IS_NATIVE_COOKIE, 'true');
        }

        const nativeEventsCookie = cookies.get(COOKIES.NATIVE_EVENTS_COOKIE);
        const isFirstLoginCookie = cookies.get(COOKIES.FIRST_LOGIN_COOKIE);
        errorNameCookie = cookies.get(COOKIES.ERROR_NAME);

        if (errorNameCookie) {
            error =
                errorMessages?.find(
                    (messageObj: { key: string | undefined }) =>
                        messageObj.key === errorNameCookie?.toLowerCase()
                ) || 'unknown_error';

            if (!error || !error.translations) {
                error = errorMessages?.find(
                    (messageObj: { key: string | undefined }) => messageObj.key === 'unknown_error'
                );
            }
        }
        const nativeEventsData = extractNativeEventDataFromCookie(nativeEventsCookie);

        if (
            !tokenSet?.accessToken &&
            !nativeEventsCookie &&
            !isFirstLoginCookie &&
            !errorNameCookie
        ) {
            sendRegistrationStartNativeEvents();
            if (market.slug === MARKET_SLUGS.SV) {
                auth?.logIn();
            }
            return;
        }

        if (nativeEventsCookie) {
            sendRegistrationEndNativeEvent(
                nativeEventsData?.playerId,
                nativeEventsData?.welcomeOfferId
            );

            window?.dataLayer.push({ event: 'fabric.register.1st_login' });

            deleteCookie(COOKIES.FIRST_LOGIN_COOKIE);
            deleteCookie(COOKIES.NATIVE_EVENTS_COOKIE);

            endOfRegistrationLogin(href, auth);
        }
    };

    onMount(async () => {
        storeDeviceInfo(cookies);

        if (isStandaloneRegistration) {
            auth = await createOrRefreshInstance(authBootstrap, market.slug, $page.url, fetch);
            await loadCasumo();
        } else {
            setTimeout(() => {
                localStorage.removeItem('data');
                loadRegily();
                isLoading = false;
                // Timeout required as window?.casumoRegily might not be set by the time checks are run
                // All this page should be re-written to add a retry mechanism to remove this timeout
            }, 1000);
        }
    });

    onDestroy(() => {
        if (browser && !isStandaloneRegistration) {
            window.removeEventListener('hashchange', handleHashChange);
        }
    });
    let title: string = 'Casumo - Create your Casumo account and start playing today';
</script>

<svelte:head>
    {#if !isStandaloneRegistration}
        <link rel="preconnect" href={getEnvironmentHost(href)} />
        <link rel="dns-prefetch" href={getEnvironmentHost(href)} />
        <link rel="preconnect" href="https://casumo-rebrand.regily.com" />
        <link rel="dns-prefetch" href="https://casumo-rebrand.regily.com" />
        <link rel="preconnect" href="https://www.google.com" />
        <link rel="dns-prefetch" href="https://www.google.com" />
        <link rel="preconnect" href="https://www.gstatic.com" />
        <link rel="dns-prefetch" href="https://www.gstatic.com" />
        <script src={regilyBundle} on:load={loadRegily}></script>
    {/if}
    <title>{title}</title>
</svelte:head>

<div class="fixed inset-0 flex flex-col">
    <RebrandRegistrationBg />

    <div class="z-10 fixed inset-0 mx-auto flex flex-col">
        <div class="hidden h-[10vh] items-center justify-center tablet:flex">
            <RebrandCasumoLogo />
        </div>
        <div class="flex flex-1 items-center justify-center">
            {#if isLoading}
                <img src={loaderImg} alt="loader" />
            {/if}
            {#if errorNameCookie}
                <RegistrationError {market} {auth} {error} {localCode} />
            {:else}
                <div class="ellipsis-container"></div>
            {/if}
        </div>
    </div>
</div>

<style>
    :global(.regily-module) {
        background: transparent !important;
        z-index: 10 !important;
    }
    :global(.regily-module-istvan iframe) {
        z-index: 10 !important;
    }
    :global(.regily-module.visible) {
        animation: none !important;
    }

    @keyframes regilyLoaderFadeInOut {
        0%,
        80%,
        100% {
            box-shadow: 0 2.5em 0 -1.3em;
        }
        40% {
            box-shadow: 0 2.5em 0 0;
        }
    }
</style>
